import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { RolePageLayout } from '../components/layout/RolePageLayout';
import { Card, Badge, Button, Spinner } from '../components/ui';
import { driverApi } from '../services/api';
import { ORDER_STATUS, ORDER_STATUS_MAP } from '../constants';

export default function DriverDashboard() {
  const { t } = useTranslation();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [trackingActive, setTrackingActive] = useState(false);
  const [serials, setSerials] = useState({}); // { orderId: "serial_number" }

  useEffect(() => {
    let qInterval;
    let trackInterval;

    fetchQueue();
    qInterval = setInterval(fetchQueue, 30000);

    if ('geolocation' in navigator) {
      setTrackingActive(true);
      trackInterval = setInterval(() => {
        navigator.geolocation.getCurrentPosition(
          async (pos) => {
            try {
              await driverApi.updateLocation('active', pos.coords.latitude, pos.coords.longitude);
            } catch (err) {
              console.error('Location sync failed:', err);
            }
          },
          (err) => console.error('GPS Error:', err),
          { enableHighAccuracy: true },
        );
      }, 30000);
    }

    return () => {
      clearInterval(qInterval);
      if (trackInterval) clearInterval(trackInterval);
      setTrackingActive(false);
    };
  }, []);

  const fetchQueue = async () => {
    try {
      const data = await driverApi.getQueue();
      setQueue(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Queue fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (orderId, status) => {
    setUpdating(orderId);
    try {
      const currentSerials = serials[orderId] ? [serials[orderId]] : [];
      await driverApi.updateStatus(orderId, status, currentSerials);
      toast.success(t('driverDashboard.statusUpdated'));
      await fetchQueue();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUpdating(null);
    }
  };

  const activeOrder = queue[0]; // Next in queue
  const remainingOrders = queue.slice(1);

  if (loading) {
    return (
      <RolePageLayout role={t('driverDashboard.role')}>
        <div className="h-[60vh] flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      </RolePageLayout>
    );
  }

  const handleActivateRoute = async () => {
    try {
      await driverApi.activateRoute('active');
      toast.success(t('driverDashboard.routeStarted'));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleCompleteRoute = async () => {
    try {
      await driverApi.completeRoute('active');
      toast.success(t('driverDashboard.routeFinished'));
      fetchQueue();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <RolePageLayout
      role={t('driverDashboard.role')}
      headerRight={
        trackingActive ? (
          <Badge variant="green" pulse className="italic">{t('driverDashboard.gpsActive')}</Badge>
        ) : (
          <Badge variant="slate">{t('driverDashboard.gpsInactive')}</Badge>
        )
      }
    >
      <div className="px-5 py-6 max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="sg-h1 text-[28px]">
            {t('driverDashboard.titlePrefix')} <span className="text-[var(--brand-green)]">{t('driverDashboard.titleSuffix')}</span>
          </h1>
          <p className="sg-label mt-1">{t('driverDashboard.subtitle')}</p>
        </div>

        <div className="grid grid-cols-2 gap-3.5 mb-8">
          <Button
            size="lg"
            variant="primary"
            className="h-[124px] rounded-[32px] shadow-lg flex flex-col gap-2 !normal-case !tracking-tight text-[15px] leading-tight"
            onClick={handleActivateRoute}
          >
            <span className="material-symbols-outlined text-[40px]">play_circle</span>
            {t('driverDashboard.startRoute')}
          </Button>

          <Button
            size="lg"
            variant="blue"
            className="h-[124px] rounded-[32px] shadow-lg flex flex-col gap-2 !normal-case !tracking-tight text-[15px] leading-tight"
            onClick={() => setTrackingActive(!trackingActive)}
          >
            <span className="material-symbols-outlined text-[40px]">{trackingActive ? 'location_off' : 'location_on'}</span>
            {trackingActive ? t('driverDashboard.gpsStop') : t('driverDashboard.gpsStart')}
          </Button>

          <Button
            size="lg"
            variant="amber"
            className="h-32 rounded-[32px] shadow-xl flex flex-col gap-2 font-black italic uppercase tracking-tighter text-slate-900"
            onClick={() => toast.info(t('driverDashboard.gasFinishedToast'))}
          >
            <span className="material-symbols-outlined text-[40px]">gas_meter</span>
            {t('driverDashboard.gasFinished')}
          </Button>

          <Button
            size="lg"
            variant="emerald"
            className="h-[124px] rounded-[32px] shadow-lg flex flex-col gap-2 !normal-case !tracking-tight text-[15px] leading-tight"
            onClick={handleCompleteRoute}
          >
            <span className="material-symbols-outlined text-[40px]">task_alt</span>
            {t('driverDashboard.finishRoute')}
          </Button>
        </div>

        <div className="flex items-center justify-between mb-4 px-1">
           <h3 className="font-black text-slate-400 uppercase text-xs tracking-[0.2em] italic">{t('driverDashboard.customerQueue')}</h3>
           <Badge variant="blue">{t('driverDashboard.count', { count: queue.length })}</Badge>
        </div>

        {/* Current Active Order (The Next Task) */}
        <AnimatePresence mode="wait">
          {activeOrder ? (
            <motion.div
              key={activeOrder.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="mb-10"
            >
              <Card className="p-8 border-2 border-[#1e9f6b] relative overflow-hidden shadow-xl">
                {/* Priority Indicator */}
                {activeOrder.isPriority && (
                  <div className="absolute top-0 right-0">
                    <div className="bg-amber-400 text-white text-[10px] font-black px-4 py-1 rotate-45 translate-x-3 translate-y-2 uppercase tracking-tighter">
                      {t('driverDashboard.priority')}
                    </div>
                  </div>
                )}

                <div className="flex flex-col md:flex-row gap-8 items-start">
                  <div className="h-20 w-20 bg-[#1e9f6b]/10 rounded-3xl flex items-center justify-center shrink-0">
                    <span className="material-symbols-outlined text-[#1e9f6b] text-[40px]">person_pin_circle</span>
                  </div>
                  
                  <div className="flex-1 space-y-4">
                    <div>
                      <Badge variant="green" className="mb-2">{t('driverDashboard.nextCustomerBadge')}</Badge>
                      <h2 className="text-2xl font-black text-slate-900 dark:text-white">
                        {activeOrder.user?.name || t('driverDashboard.noName')}
                      </h2>
                      <p className="text-slate-500 flex items-center gap-2 mt-1">
                        <span className="material-symbols-outlined text-[18px]">call</span>
                        {activeOrder.user?.phone || t('driverDashboard.noPhone')}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4 py-4 border-y border-slate-100 dark:border-slate-800">
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase">{t('driverDashboard.amount')}</p>
                        <p className="font-black text-slate-900 dark:text-white text-lg">{t('driverDashboard.cylinderCount', { count: activeOrder.quantity })}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase">{t('driverDashboard.payment')}</p>
                        <p className="font-black text-[#1e9f6b] text-lg">
                          {(activeOrder.totalPrice || 0).toLocaleString()} {t('common.uzs')}
                        </p>
                      </div>
                    </div>

                    {/* Action Workflow */}
                    <div className="flex flex-wrap gap-3 pt-2">
                      {activeOrder.status === ORDER_STATUS.PENDING && (
                        <Button 
                          onClick={() => handleStatusUpdate(activeOrder.id, ORDER_STATUS.ON_WAY)}
                          loading={updating === activeOrder.id}
                          icon="local_shipping" 
                          className="flex-1"
                        >
                          {t('driverDashboard.wentOnWay')}
                        </Button>
                      )}

                      {activeOrder.status === ORDER_STATUS.ON_WAY && (
                        <Button
                          onClick={() => handleStatusUpdate(activeOrder.id, ORDER_STATUS.ARRIVED)}
                          loading={updating === activeOrder.id}
                          variant="emerald"
                          icon="location_on"
                          className="flex-1"
                        >
                          {t('driverDashboard.arrived')}
                        </Button>
                      )}

                      {activeOrder.status === ORDER_STATUS.ARRIVED && (
                        <div className="w-full space-y-3">
                          <div className="relative">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400">qr_code_scanner</span>
                            <input
                              type="text"
                              placeholder={t('driverDashboard.serialPlaceholder')}
                              className="w-full pl-12 pr-4 py-3 bg-slate-50 dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 rounded-2xl focus:border-[#1e9f6b] outline-none transition-all font-bold"
                              value={serials[activeOrder.id] || ''}
                              onChange={(e) => setSerials(prev => ({ ...prev, [activeOrder.id]: e.target.value }))}
                            />
                          </div>
                          <Button
                            onClick={() => handleStatusUpdate(activeOrder.id, ORDER_STATUS.COMPLETED)}
                            loading={updating === activeOrder.id}
                            disabled={!serials[activeOrder.id]}
                            variant="green"
                            icon="check_circle"
                            className="w-full"
                          >
                            {t('driverDashboard.delivered')}
                          </Button>
                        </div>
                      )}

                      {activeOrder.status !== ORDER_STATUS.ARRIVED && (
                        <Button
                          onClick={() => handleStatusUpdate(activeOrder.id, ORDER_STATUS.SKIPPED)}
                          loading={updating === activeOrder.id}
                          variant="outline"
                          icon="person_off"
                          className="text-slate-500"
                        >
                          {t('driverDashboard.notFound')}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          ) : (
            <div className="py-20 text-center">
              <span className="material-symbols-outlined text-[64px] text-slate-200">check_circle</span>
              <p className="text-slate-400 mt-4 font-medium">{t('driverDashboard.allDone')}</p>
            </div>
          )}
        </AnimatePresence>

        {/* Remaining Queue */}
        <div className="space-y-4">
          <h3 className="font-bold text-slate-500 uppercase text-xs tracking-widest ml-1">{t('driverDashboard.nextInQueue')}</h3>
          {remainingOrders.length === 0 ? (
            <p className="text-sm text-slate-400 italic ml-1">{t('driverDashboard.noOtherOrders')}</p>
          ) : (
            <div className="grid gap-3">
              {remainingOrders.map((order, idx) => (
                <Card key={order.id} className="p-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 bg-slate-100 dark:bg-slate-800 rounded-xl flex items-center justify-center font-black text-slate-500">
                      {idx + 2}
                    </div>
                    <div>
                      <p className="font-bold text-slate-900 dark:text-white">{order.user?.name}</p>
                      <p className="text-xs text-slate-500">{t('driverDashboard.cylinderCount', { count: order.quantity })}</p>
                    </div>
                  </div>
                  <Badge variant={ORDER_STATUS_MAP[order.status]?.color || 'slate'}>
                    {ORDER_STATUS_MAP[order.status]?.label}
                  </Badge>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </RolePageLayout>
  );
}
